import { createFileRoute } from "@tanstack/react-router";
import { DocPage } from "@/components/DocPage";
import { useState } from "react";
import { ChevronDown } from "lucide-react";

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "FAQ — Want To Buy" },
      { name: "description", content: "How the Want To Buy connection platform works: buy orders, the mandatory 2-photo scam filter, PayPal Goods & Services, and how to handle a transaction problem." },
    ],
  }),
  component: FaqPage,
});

const FAQ: { q: string; a: string }[] = [
  {
    q: "What is Want To Buy?",
    a: "Want To Buy is a connection platform for TCG collectors. Its sole purpose is to connect buyers with sellers who physically hold the cards those buyers are looking for. Want To Buy does not process payments, hold funds, verify card authenticity, guarantee condition, or mediate disputes. All transactions are arranged entirely between the buyer and the seller."
  },
  {
    q: "How does a buy order work?",
    a: "You post the card, condition, and the price you're willing to pay. Sellers who own the card submit offers. When you accept an offer, the seller must submit 2 mandatory timestamped photos before the message thread opens between you and the seller. From that point on, you arrange payment and shipping directly with the seller."
  },
  {
    q: "What are the 2 mandatory timestamped photos?",
    a: "Photo 1: the front of the card with a handwritten note visible in the same frame. Photo 2: the back of the card with a handwritten note visible in the same frame. Each handwritten note must contain — written by hand — today's date in DD/MM/YYYY format, Seller: <username>, Buyer: <username>, the order ID, and the card condition. For graded cards, one of the two photos must also show the slab label with the cert number legible. This is a scam-prevention filter that proves the seller physically possesses the card. Want To Buy does not authenticate or grade cards from these photos — the buyer must evaluate the photos themselves."
  },
  {
    q: "Can I request more photos before proceeding?",
    a: "Yes — after the seller submits the 2 mandatory photos, you can request additional photos in the message thread. The seller has 24 hours to respond to additional photo requests. Requesting more photos is optional and at your discretion. Additional photos do not require a new handwritten timestamp."
  },
  {
    q: "How do payments work on Want To Buy?",
    a: "Want To Buy does not process payments. After you and the seller are connected through the message thread, you arrange payment directly between yourselves. Want To Buy strongly recommends PayPal Goods & Services because PayPal — not Want To Buy — provides independent buyer and seller protection through their own terms."
  },
  {
    q: "What happens if a transaction goes wrong?",
    a: "Want To Buy does not mediate disputes. If a transaction goes wrong, resolve it directly with your payment provider. If you paid via PayPal Goods & Services, open a dispute in PayPal's Resolution Centre. Screenshots of your Want To Buy message thread — including the buy order, the mandatory timestamped photos, and all shipping communications — are accepted as evidence in PayPal Significantly Not as Described and Item Not Received disputes."
  },
  {
    q: "How do I use my message thread as dispute evidence?",
    a: "Before opening a PayPal dispute: (1) screenshot the buy order page showing the agreed card details, (2) screenshot the 2 mandatory timestamped photos submitted by the seller, (3) screenshot the full message thread showing agreed price and condition, and (4) submit these screenshots as evidence in your PayPal Goods & Services dispute. Want To Buy will not release transaction records to third parties except when legally required by court order or law enforcement request — the screenshots you take from your own account are the primary evidence channel."
  },
  {
    q: "Does Want To Buy verify sellers or authenticate cards?",
    a: "No. The mandatory 2-photo requirement is a scam-prevention filter that proves the seller physically holds the card — nothing more. Want To Buy does not authenticate cards, verify grades, or verify condition claims. Evaluating the seller and the photos is entirely the buyer's responsibility. Buyer-protection safety comes from using PayPal Goods & Services."
  },
  {
    q: "How does cert verification work for graded cards?",
    a: "For graded cards, sellers provide the cert number, grading company, and a direct link to the cert lookup page. One of the 2 mandatory timestamped photos must show the slab label with the cert number legible. Verify the cert link yourself before proceeding — Want To Buy does not automatically verify certs against any grading database."
  },
  {
    q: "How do I become a Trusted Seller?",
    a: "Complete 100+ transactions with a 99%+ clean rating (percentage of completed transactions without strikes). Trusted Sellers get a visible badge and priority on tied-price offers. Status is suspended automatically if your rating drops below 97% and reinstated when it recovers."
  },
  {
    q: "How much does Want To Buy charge?",
    a: "Nothing — Want To Buy is currently free to use. There is no platform fee on transactions, no listing fees, and no monthly minimums. Buyers pay the agreed price + shipping directly to the seller, and the seller receives the full amount. Full details on the Fees page."
  },
  {
    q: "What if a seller ghosts or refuses to submit the mandatory photos?",
    a: "Sellers have 48 hours after offer acceptance to submit the 2 mandatory photos. If they fail to submit within that window, the order returns to open, the buyer is not connected, and the seller receives a strike. Sellers who refuse the photo requirement cannot proceed — the requirement is non-negotiable."
  },
  {
    q: "Can I cancel a buy order?",
    a: "Yes — open buy orders with no accepted offers can be cancelled from your dashboard at any time. Once you accept an offer, the seller has 48 hours to submit the mandatory photos; if they don't, the order returns to open. Once you're connected in the message thread, everything is between you and the seller."
  },
  {
    q: "Can I arrange card trades on Want To Buy?",
    a: "Card-for-card trades are outside the scope of the connection platform. If you arrange a trade through the messaging system, you do so entirely at your own risk. For card-for-card swaps we recommend a dedicated trade community."
  },
];

function FaqPage() {
  return (
    <DocPage eyebrow="Help" title="Frequently asked questions" intro="How the Want To Buy connection platform works. For anything else, use the Contact & Support page.">
      <div className="space-y-3 !mt-0">
        {FAQ.map((item, i) => <FaqRow key={i} {...item} />)}
      </div>
    </DocPage>
  );
}

function FaqRow({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-md border border-border bg-panel/60">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm font-semibold"
      >
        {q}
        <ChevronDown className={`h-4 w-4 shrink-0 transition ${open ? "rotate-180 text-primary" : "text-muted-foreground"}`} />
      </button>
      {open && <div className="border-t border-border px-4 py-3 text-sm text-muted-foreground whitespace-pre-line">{a}</div>}
    </div>
  );
}
